import React from "react";
import { PropError } from "../PropX";
import * as SelectMenu from "../SelectMenu_v2";
import { Spacer } from "../Spacer";
import { TextInput } from "../TextInput_v2";
import { LoadingState } from "../states/LoadingState";
import { maybe } from "../utils/Maybe";
import { CreatePageBlock } from "./CreatePageBlock";
import { CreatePageFinalizeState } from "./CreatePageFinalizeState";

type ResourceNameMeta = {
  /**
   * Used in the text input label to describe what kind of resource you are naming.
   * For example, "Choose a unique _database cluster_ name." where "database cluster" is the resource name.
   *
   * Does not default to anything and if not provided the label will be "Choose a unique name."
   */
  kind: string;
  /**
   * A note to display under the resource name input when there is no error present.
   */
  note: React.ReactNode;
};

type ResourceNameInputProps = {
  state: CreatePageFinalizeState["resourceName"];
  meta: Partial<ResourceNameMeta>;
};

const ResourceNameInput: React.FC<ResourceNameInputProps> = (props) => {
  const { state, meta } = props;

  if (maybe.is.none(state)) {
    return null;
  }

  const { value } = state;

  const error: PropError = React.useMemo(() => {
    return {
      ...value.error,
      validationMessages: {
        patternMismatch: value.patternMismatchMessage,
      },
    };
  }, [value.error?.message, value.patternMismatchMessage]);

  const textInputLabel = React.useMemo(() => {
    return {
      text: `Choose a unique ${meta.kind ? `${meta.kind} ` : ""}name`,
      subtext: `Names must be in lowercase. They can be between ${value.minLength} and ${value.maxLength} characters long and may contain dashes.`,
    };
  }, [meta.kind, value.minLength, value.maxLength]);

  return (
    <>
      <TextInput.Search
        ref={value.ref}
        name={value.name}
        value={value.value}
        onChange={(ev) => value.onChange(ev.target.value)}
        required
        autoComplete="off"
        label={textInputLabel}
        note={meta.note}
        error={error}
        minLength={value.minLength}
        maxLength={value.maxLength}
        pattern={value.pattern}
        loading={value.validating}
      />
      <Spacer space={4} />
    </>
  );
};

type ProjectsInputProps = {
  state: CreatePageFinalizeState["projects"];
};

const ProjectsInput: React.FC<ProjectsInputProps> = (props) => {
  const { state } = props;

  switch (state.status) {
    case "loading":
    case "error": // TODO: display proper error state
      return (
        <>
          <SelectMenu.BaseSkeleton name="" label="Select a project" />
          <Spacer space={6} />
        </>
      );
    case "ready": {
      const { data } = state;

      if (maybe.is.none(data)) {
        return null;
      }

      const { name, options, selected, onSelect } = data.value;

      return (
        <>
          <SelectMenu.Base name={name} label="Select a project" options={options} selected={selected} onSelect={onSelect} />
          <Spacer space={4} />
        </>
      );
    }
  }
};

const tokenInputLabel = {
  text: "Tags",
  subtext: "May contain letters, numbers, colons, dashes, and underscores.",
};

type TagsInputProps = {
  state: CreatePageFinalizeState["tags"];
};

const TagsInput: React.FC<TagsInputProps> = (props) => {
  const { state } = props;

  if (state.type === "none") {
    return null;
  }

  const { name, search, selected, onChangeSearch, onSelectMany, data } = state.value;

  switch (data.status) {
    case "error":
      return (
        /**
         * TODO: need to display an error or something.
         */
        <TextInput.DynamicToken
          name={name}
          label={tokenInputLabel}
          options={[]}
          search={search}
          selected={selected}
          onChangeSearch={onChangeSearch}
          onSelectMany={onSelectMany}
          placeholder="Enter tag name"
          pattern="^[a-zA-Z0-9:_-]+$"
          disabled
        />
      );
    case "loading":
      return (
        <TextInput.DynamicToken
          name={name}
          label={tokenInputLabel}
          options={[]}
          search={search}
          selected={selected}
          onChangeSearch={onChangeSearch}
          onSelectMany={onSelectMany}
          placeholder="Enter tag name"
          pattern="^[a-zA-Z0-9:_-]+$"
        />
      );
    case "ready": {
      const { searching, options } = data.data;

      return (
        <TextInput.DynamicToken
          name={name}
          label={tokenInputLabel}
          options={options}
          search={search}
          selected={selected}
          onChangeSearch={onChangeSearch}
          onSelectMany={onSelectMany}
          filterOptions={false}
          placeholder="Enter tag name"
          pattern="^[a-zA-Z0-9:_-]+$"
          rightSide={searching ? <LoadingState /> : undefined}
        />
      );
    }
  }
};

const defaultFinalizeLabel = { text: "Finalize and create" };

type Props = {
  /**
   * The state of the finalize section. See `CreatePage.createFinalizeState` for more details.
   */
  state: CreatePageFinalizeState;

  /**
   * Meta information for the display of the resource name input.
   */
  resourceNameMeta?: Partial<ResourceNameMeta>;

  UNSAFE_finalizeLabel?: {
    text: string;
  };
};

const propsAreEqual = (prevProps: Props, nextProps: Props) => {
  return prevProps.state === nextProps.state;
};

export const CreatePageFinalize: React.FC<Props> = React.memo((props) => {
  const { state, resourceNameMeta = {}, UNSAFE_finalizeLabel = defaultFinalizeLabel } = props;

  return (
    <CreatePageBlock fieldset label={UNSAFE_finalizeLabel}>
      <ResourceNameInput state={state.resourceName} meta={resourceNameMeta} />
      <ProjectsInput state={state.projects} />
      <TagsInput state={state.tags} />
    </CreatePageBlock>
  );
}, propsAreEqual);

CreatePageFinalize.displayName = "CreatePageFinalize";
